/**
 * BEX LOOP STUDIO - entry point.
 *
 * Boots the shell, wires global error handling, and makes it obvious in
 * the UI when something is unsupported rather than failing silently.
 */

import { createApp } from './ui/app-shell.js';
import { toastError, toastWarn } from './ui/components/toast.js';
import { setLogLevel, createLogger } from './utils/log.js';
import { BRAND } from './core/config.js';

const log = createLogger('boot');

// Show debug lines when the URL carries ?debug=1 - useful on a phone
// where opening devtools is awkward.
const params = new URLSearchParams(location.search);
if (params.get('debug') === '1') setLogLevel('debug');

function fatal(message, detail) {
  const view = document.getElementById('view');
  if (!view) return;
  view.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'stack';
  const title = document.createElement('h2');
  title.textContent = message;
  const body = document.createElement('pre');
  body.className = 'prompt-box';
  body.textContent = String(detail ?? '');
  const hint = document.createElement('div');
  hint.className = 'note note--info';
  hint.textContent =
    'This usually means the browser is missing a required API. Chrome or a Chromium browser on Android is the primary target.';
  wrap.append(title, hint, body);
  view.append(wrap);
}

window.addEventListener('error', (event) => {
  log.error('uncaught error', event.error ?? event.message);
  toastError(`Unexpected error: ${event.error?.message ?? event.message}`);
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  log.error('unhandled rejection', reason);
  toastError(`Unexpected error: ${reason?.message ?? String(reason)}`);
});

// Warn before losing an in-progress real-time render.
window.addEventListener('beforeunload', (event) => {
  if (window.__bexRendering) {
    event.preventDefault();
    event.returnValue = '';
  }
});

async function boot() {
  if (typeof MediaRecorder === 'undefined') {
    toastWarn('This browser has no MediaRecorder, so export is unavailable. Everything else still works.');
  }
  const app = await createApp();
  window.__bexApp = app;

  // Keep the beforeunload guard in sync with the render state.
  const sync = (state) => {
    window.__bexRendering = state.render?.status === 'rendering' || state.render?.status === 'preparing';
  };
  app.ctx.store.subscribe(sync, { immediate: true });

  log.info(`${BRAND.appFull} ${BRAND.version} ready`);
}

boot().catch((error) => {
  log.error('boot failed', error);
  fatal('BEX LOOP could not start', error?.stack ?? error?.message ?? String(error));
});
